import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { api, ApiError } from "../api";

interface Job {
  job_id: string;
  title: string;
  company: string;
  location?: string;
  url: string;
  status: string;
  score?: number;
  decision?: string;
  jd_text?: string;
  fetch_error?: string;
  fit_summary?: string;
  fit_strengths?: string[];
  fit_gaps?: string[];
  emphasis_hints?: string[];
  salary_text?: string;
  salary_source?: string;
  salary_below_min?: boolean;
}

type TailorStatus = "tailoring" | "rendering" | "ready" | "error";

interface Application {
  resume_pdf_s3_key?: string;
  cover_pdf_s3_key?: string;
  headline?: string;
  tailor_status?: TailorStatus;
  tailor_version?: number;
  tailor_error?: string;
  tailor_started_at?: string;
}

interface DetailResponse {
  job: Job;
  application?: Application;
}

export function JobDetail() {
  const { jobId } = useParams();
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => api<DetailResponse>(`/jobs/${jobId}`, token),
    enabled: !!token && !!jobId,
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      if (["new", "fetched", "scored"].includes(d.job.status)) return 5000;
      if (d.application?.tailor_status === "tailoring" || d.application?.tailor_status === "rendering")
        return 3000;
      return false;
    },
  });

  const skip = useMutation({
    mutationFn: () => api(`/jobs/${jobId}/skip`, token, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job", jobId] }),
  });

  const regen = useMutation({
    mutationFn: () => api(`/jobs/${jobId}/regenerate`, token, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      qc.invalidateQueries({ queryKey: ["doc-url", jobId] });
      qc.invalidateQueries({ queryKey: ["doc-blob", jobId] });
    },
  });

  const pasteJd = useMutation({
    mutationFn: (text: string) =>
      api(`/jobs/${jobId}/jd`, token, { method: "PUT", body: JSON.stringify({ jd_text: text }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job", jobId] }),
  });

  if (q.isPending) return <p className="text-slate-400">Loading…</p>;
  if (q.error) {
    const status = q.error instanceof ApiError ? q.error.status : 0;
    return (
      <div className="space-y-2">
        <Link to="/inbox" className="text-xs text-slate-400 hover:text-slate-200">
          ← Inbox
        </Link>
        <p className="text-rose-300">
          {status === 404 ? "Job not found." : (q.error as Error).message}
        </p>
      </div>
    );
  }
  if (!q.data) return null;

  const { job, application } = q.data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/inbox" className="text-xs text-slate-400 hover:text-slate-200">
          ← Inbox
        </Link>
      </div>

      <header className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-50">{job.title}</h1>
            <p className="mt-0.5 text-sm text-slate-300">
              {job.company}
              {job.location && <span className="text-slate-500"> · {job.location}</span>}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {job.salary_text && (
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm ${
                  job.salary_below_min
                    ? "bg-amber-900/40 text-amber-200"
                    : "bg-slate-800 text-slate-300"
                }`}
                title={
                  job.salary_below_min
                    ? "Below your minimum salary setting"
                    : job.salary_source === "jd_text"
                    ? "Scraped from the job description"
                    : "From the posting's structured data"
                }
              >
                {job.salary_text}
                {job.salary_below_min && " · below your minimum"}
              </span>
            )}
            {job.score != null && (
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                  job.score >= 75
                    ? "bg-emerald-900/40 text-emerald-200"
                    : job.score >= 50
                    ? "bg-amber-900/40 text-amber-200"
                    : "bg-slate-800 text-slate-300"
                }`}
              >
                Score {job.score}
              </span>
            )}
            <span className="rounded-full bg-slate-800 px-3 py-1 text-sm text-slate-300">
              {job.status}
            </span>
            <a
              href={job.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-200 transition hover:border-slate-500"
            >
              Open on LinkedIn ↗
            </a>
          </div>
        </div>
      </header>

      <Actions
        job={job}
        onSkip={() => skip.mutate()}
        onRegen={() => regen.mutate()}
        skipPending={skip.isPending}
        regenPending={
          regen.isPending ||
          application?.tailor_status === "tailoring" ||
          application?.tailor_status === "rendering"
        }
        skipError={skip.error as Error | null}
        regenError={regen.error as Error | null}
      />

      {job.status === "fetch_failed" && (
        <PasteJdCard
          error={job.fetch_error}
          onSubmit={(t) => pasteJd.mutate(t)}
          submitting={pasteJd.isPending}
          submitError={pasteJd.error as Error | null}
        />
      )}

      {(job.fit_summary || job.fit_strengths?.length || job.fit_gaps?.length) && (
        <FitCard job={job} />
      )}

      {application &&
        (application.resume_pdf_s3_key ||
          application.cover_pdf_s3_key ||
          application.tailor_status) && (
          <DocsCard jobId={job.job_id} application={application} />
        )}

      {job.jd_text && (
        <section className="space-y-2 rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Job description
          </h2>
          <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-200">
            {job.jd_text}
          </div>
        </section>
      )}
    </div>
  );
}

function Actions({
  job,
  onSkip,
  onRegen,
  skipPending,
  regenPending,
  skipError,
  regenError,
}: {
  job: Job;
  onSkip: () => void;
  onRegen: () => void;
  skipPending: boolean;
  regenPending: boolean;
  skipError: Error | null;
  regenError: Error | null;
}) {
  return (
    <section className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 p-4">
      <button
        type="button"
        className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-200 transition hover:border-slate-500 hover:bg-slate-800 disabled:opacity-50"
        onClick={onSkip}
        disabled={skipPending || job.status === "skipped"}
      >
        {skipPending ? "Skipping…" : "Skip"}
      </button>
      <button
        type="button"
        className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-200 transition hover:border-slate-500 hover:bg-slate-800 disabled:opacity-50"
        onClick={onRegen}
        disabled={regenPending || job.status === "fetch_failed" || !job.jd_text}
      >
        {regenPending ? "Regenerating…" : "Regenerate tailored docs"}
      </button>
      {(skipError || regenError) && (
        <span className="text-xs text-rose-300">
          {(skipError ?? regenError)?.message}
        </span>
      )}
    </section>
  );
}

function FitCard({ job }: { job: Job }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-800 bg-slate-900 p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Fit analysis</h2>
      {job.fit_summary && <p className="text-sm text-slate-200">{job.fit_summary}</p>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <BulletList title="Strengths" items={job.fit_strengths} className="text-emerald-200" />
        <BulletList title="Gaps" items={job.fit_gaps} className="text-rose-200" />
        <BulletList title="Emphasis hints" items={job.emphasis_hints} className="text-cyan-200" />
      </div>
    </section>
  );
}

function BulletList({
  title,
  items,
  className,
}: {
  title: string;
  items?: string[];
  className?: string;
}) {
  if (!items?.length) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      <ul className={`mt-1 space-y-1 text-xs ${className ?? "text-slate-200"}`}>
        {items.map((it, i) => (
          <li key={i}>• {it}</li>
        ))}
      </ul>
    </div>
  );
}

function PasteJdCard({
  error,
  onSubmit,
  submitting,
  submitError,
}: {
  error?: string;
  onSubmit: (t: string) => void;
  submitting: boolean;
  submitError: Error | null;
}) {
  const [text, setText] = useState("");
  return (
    <section className="space-y-3 rounded-xl border border-amber-800 bg-amber-950/30 p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-200">
        Auto-fetch failed
      </h2>
      {error && <p className="text-xs text-amber-200/80">{error}</p>}
      <textarea
        rows={10}
        placeholder="Paste the full job description here…"
        className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-slate-500"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">{text.length} chars (need ≥ 100)</p>
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          onClick={() => onSubmit(text)}
          disabled={submitting || text.length < 100}
        >
          {submitting ? "Saving…" : "Save JD"}
        </button>
      </div>
      {submitError && <p className="text-xs text-rose-300">{submitError.message}</p>}
    </section>
  );
}

type DocKind = "resume.pdf" | "cover.pdf" | "resume.json" | "cover.json" | "provenance";

interface DocUrlResponse {
  url: string;
  kind: string;
  download: boolean;
  filename: string;
  tailor_version?: number;
}

const TABS: { kind: DocKind; label: string }[] = [
  { kind: "resume.pdf", label: "Resume PDF" },
  { kind: "cover.pdf", label: "Cover PDF" },
  { kind: "resume.json", label: "Resume JSON" },
  { kind: "cover.json", label: "Cover JSON" },
  { kind: "provenance", label: "Provenance" },
];

function DocsCard({ jobId, application }: { jobId: string; application: Application }) {
  const [active, setActive] = useState<DocKind>("resume.pdf");
  const status = application.tailor_status;
  const inFlight = status === "tailoring" || status === "rendering";
  const hasDocs = !!(application.resume_pdf_s3_key || application.cover_pdf_s3_key);
  const lastGenerated = application.tailor_version
    ? new Date(application.tailor_version)
    : null;

  return (
    <section className="space-y-3 rounded-xl border border-emerald-800 bg-emerald-950/10 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-200">
            Tailored documents
          </h2>
          {lastGenerated && (
            <p className="mt-1 text-xs text-slate-400">
              Last generated{" "}
              <time dateTime={lastGenerated.toISOString()} title={lastGenerated.toLocaleString()}>
                {formatRelative(lastGenerated)}
              </time>
            </p>
          )}
        </div>
        {application.headline && (
          <p className="max-w-lg text-right text-xs italic text-slate-400">
            "{application.headline}"
          </p>
        )}
      </div>

      {inFlight && (
        <div className="flex items-center gap-3 rounded-md border border-emerald-800/60 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-100">
          <Spinner />
          <span>
            {status === "tailoring"
              ? "Tailoring with Claude… (10–30s)"
              : "Rendering PDFs…"}
          </span>
          {application.tailor_started_at && (
            <span className="ml-auto text-slate-400">
              Started {formatRelative(new Date(application.tailor_started_at))}
            </span>
          )}
        </div>
      )}
      {status === "error" && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-3 py-2 text-xs text-rose-200">
          Regeneration failed: {application.tailor_error ?? "unknown error"}
        </div>
      )}

      {hasDocs && (
        <>
          <div className="flex flex-wrap gap-1 border-b border-slate-800">
            {TABS.map((t) => (
              <button
                key={t.kind}
                type="button"
                onClick={() => setActive(t.kind)}
                className={`-mb-px rounded-t-md px-3 py-1.5 text-xs transition ${
                  active === t.kind
                    ? "border border-b-transparent border-slate-700 bg-slate-900 text-slate-50"
                    : "border border-transparent text-slate-400 hover:text-slate-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <DocPane jobId={jobId} kind={active} version={application.tailor_version} />
        </>
      )}
    </section>
  );
}

function formatRelative(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-emerald-300 border-t-transparent"
    />
  );
}

function DocPane({
  jobId,
  kind,
  version,
}: {
  jobId: string;
  kind: DocKind;
  version?: number;
}) {
  if (kind === "resume.pdf" || kind === "cover.pdf")
    return <PdfPane jobId={jobId} kind={kind} version={version} />;
  if (kind === "resume.json" || kind === "cover.json")
    return <JsonPane jobId={jobId} kind={kind} version={version} />;
  return <ProvenancePane jobId={jobId} version={version} />;
}

function useDocUrl(
  jobId: string,
  kind: string,
  download = false,
  enabled = true,
  version?: number,
) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  return useQuery({
    queryKey: ["doc-url", jobId, kind, download, version ?? null],
    queryFn: () =>
      api<DocUrlResponse>(`/jobs/${jobId}/docs/${kind}${download ? "?download=1" : ""}`, token),
    enabled: !!token && enabled,
    staleTime: 8 * 60_000,
  });
}

function ToolbarButtons({
  jobId,
  kind,
}: {
  jobId: string;
  kind: string;
}) {
  const downloadQ = useDocUrl(jobId, kind, true, false);
  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-200 transition hover:border-slate-500 hover:bg-slate-800 disabled:opacity-50"
        disabled={downloadQ.isFetching}
        onClick={async () => {
          const res = await downloadQ.refetch();
          if (res.data?.url) {
            const a = document.createElement("a");
            a.href = res.data.url;
            a.download = res.data.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }
        }}
      >
        {downloadQ.isFetching ? "Preparing…" : "Download ⬇"}
      </button>
    </div>
  );
}

function PdfPane({
  jobId,
  kind,
  version,
}: {
  jobId: string;
  kind: "resume.pdf" | "cover.pdf";
  version?: number;
}) {
  const urlQ = useDocUrl(jobId, kind, false, true, version);
  const v = urlQ.data?.tailor_version ?? version;
  const iframeSrc = urlQ.data?.url
    ? v != null
      ? `${urlQ.data.url}#v=${v}`
      : urlQ.data.url
    : "";

  return (
    <div className="space-y-2">
      <ToolbarButtons jobId={jobId} kind={kind} />
      <div className="overflow-hidden rounded-md border border-slate-800 bg-slate-950">
        {urlQ.isPending && (
          <div className="flex h-[75vh] items-center justify-center text-slate-500">Loading…</div>
        )}
        {urlQ.error && (
          <div className="flex h-[75vh] items-center justify-center px-6 text-rose-300">
            {(urlQ.error as Error).message}
          </div>
        )}
        {iframeSrc && (
          <iframe src={iframeSrc} title={kind} loading="lazy" className="h-[75vh] w-full" />
        )}
      </div>
    </div>
  );
}

function JsonPane({
  jobId,
  kind,
  version,
}: {
  jobId: string;
  kind: "resume.json" | "cover.json";
  version?: number;
}) {
  const urlQ = useDocUrl(jobId, kind, false, true, version);
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();
  const v = urlQ.data?.tailor_version ?? version;

  const blobQ = useQuery({
    queryKey: ["doc-blob", jobId, kind, urlQ.data?.url, v ?? null],
    enabled: !!urlQ.data?.url,
    queryFn: async () => {
      const res = await fetch(urlQ.data!.url);
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      return (await res.json()) as Record<string, unknown>;
    },
    staleTime: 8 * 60_000,
  });

  const [draft, setDraft] = useState<string>("");
  const [dirty, setDirty] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (blobQ.data && !dirty) {
      setDraft(JSON.stringify(blobQ.data, null, 2));
      setParseError(null);
    }
  }, [blobQ.data, dirty]);

  const save = useMutation({
    mutationFn: async () => {
      // Local parse first for friendly UI error
      try {
        JSON.parse(draft);
      } catch (e) {
        throw new Error(`invalid JSON: ${(e as Error).message}`);
      }
      return api<{ ok: boolean; render_invoked: boolean }>(
        `/jobs/${jobId}/docs/${kind}`,
        token,
        { method: "PUT", body: draft },
      );
    },
    onSuccess: () => {
      setDirty(false);
      setParseError(null);
      // Invalidate everything tied to this doc so URL + blob + PDFs refresh.
      qc.invalidateQueries({ queryKey: ["doc-url", jobId] });
      qc.invalidateQueries({ queryKey: ["doc-blob", jobId] });
      qc.invalidateQueries({ queryKey: ["job", jobId] });
    },
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          Edit then Save. Strict schema validation runs server-side; PDFs re-render automatically.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 transition hover:border-slate-500 hover:bg-slate-800 disabled:opacity-50"
            disabled={!dirty || save.isPending}
            onClick={() => {
              if (blobQ.data) setDraft(JSON.stringify(blobQ.data, null, 2));
              setDirty(false);
              setParseError(null);
              save.reset();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save & re-render"}
          </button>
          <ToolbarButtons jobId={jobId} kind={kind} />
        </div>
      </div>

      {(parseError || save.error) && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-3 py-2 text-xs text-rose-200">
          {parseError ?? (save.error as Error).message}
        </div>
      )}
      {save.isSuccess && !dirty && (
        <div className="rounded-md border border-emerald-700 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-200">
          Saved. PDF tabs will refresh shortly.
        </div>
      )}

      <div className="rounded-md border border-slate-800 bg-slate-950">
        {blobQ.isPending && <p className="p-3 text-slate-500">Loading…</p>}
        {blobQ.error && <p className="p-3 text-rose-300">{(blobQ.error as Error).message}</p>}
        {blobQ.data && (
          <textarea
            spellCheck={false}
            className="h-[70vh] w-full resize-none bg-transparent p-3 font-mono text-xs text-slate-100 outline-none"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDirty(true);
              try {
                JSON.parse(e.target.value);
                setParseError(null);
              } catch (err) {
                setParseError((err as Error).message);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

function ProvenancePane({ jobId, version }: { jobId: string; version?: number }) {
  const urlQ = useDocUrl(jobId, "resume.json", false, true, version);
  const v = urlQ.data?.tailor_version ?? version;
  const blobQ = useQuery({
    queryKey: ["doc-blob", jobId, "resume.json", urlQ.data?.url, v ?? null],
    enabled: !!urlQ.data?.url,
    queryFn: async () => {
      const res = await fetch(urlQ.data!.url);
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      return (await res.json()) as { claim_provenance?: Record<string, string> };
    },
    staleTime: 8 * 60_000,
  });

  const map = blobQ.data?.claim_provenance ?? {};
  const entries = Object.entries(map);

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-400">
        Every tailored claim → its source string in the master profile. Server already verifies
        these; this view is for your trust check.
      </p>
      <div className="max-h-[75vh] overflow-auto rounded-md border border-slate-800 bg-slate-950">
        {blobQ.isPending && <p className="p-4 text-slate-500">Loading…</p>}
        {blobQ.error && <p className="p-4 text-rose-300">{(blobQ.error as Error).message}</p>}
        {blobQ.data && entries.length === 0 && (
          <p className="p-4 text-sm text-slate-500">No provenance entries (model omitted the map).</p>
        )}
        {entries.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Tailored claim</th>
                <th className="px-3 py-2 text-left">Source in master profile</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {entries.map(([claim, source], i) => (
                <tr key={i} className="align-top">
                  <td className="px-3 py-2 text-slate-100">{claim}</td>
                  <td className="px-3 py-2 text-slate-400">{source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
